/** Host-neutral parsing of realtime frames.
 *
 * This module deliberately returns domain effects instead of touching Zustand,
 * timers, CustomEvent, or a socket. Hosts decide which effects are authorized
 * for their active context and how to apply them.
 */
export type RealtimeFrame = { type: string; [key: string]: unknown };

export interface MessageFrame {
  id: string;
  channel_id: string;
  sender_id: string;
  content: string;
  content_type: 'text' | 'image' | 'command' | 'task' | 'tool_card' | 'epoch';
  reply_to_id: string | null;
  created_at: number;
  edited_at: number | null;
  [key: string]: unknown;
}

export type RealtimeEffect =
  | { kind: 'message.received'; message: MessageFrame }
  | { kind: 'message.acknowledged'; clientMessageId: string; message: MessageFrame }
  | { kind: 'message.rejected'; clientMessageId: string }
  | { kind: 'message.edited'; message: MessageFrame }
  | { kind: 'message.deleted'; channelId: string; messageId: string; deletedAt: number }
  | { kind: 'typing.changed'; channelId: string; userId: string; displayName: string }
  | { kind: 'reaction.updated'; channelId: string; messageId: string; reactions: unknown[] }
  | { kind: 'agent.activity'; frame: RealtimeFrame };

const object = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const string = (value: unknown): value is string => typeof value === 'string' && value.length > 0;
const contentTypes = new Set<MessageFrame['content_type']>(['text', 'image', 'command', 'task', 'tool_card', 'epoch']);

function message(value: unknown): value is MessageFrame {
  return object(value) && string(value.id) && string(value.channel_id) && string(value.sender_id) &&
    typeof value.content === 'string' && typeof value.content_type === 'string' && contentTypes.has(value.content_type as MessageFrame['content_type']) &&
    (typeof value.reply_to_id === 'string' || value.reply_to_id === null) && typeof value.created_at === 'number' &&
    (typeof value.edited_at === 'number' || value.edited_at === null);
}

/** Turns recognized, structurally valid wire frames into host-applicable effects. */
export function reduceRealtimeFrame(frame: RealtimeFrame): RealtimeEffect[] {
  switch (frame.type) {
    case 'new_message':
      return message(frame.message) ? [{ kind: 'message.received', message: frame.message }] : [];
    case 'message_ack':
      return string(frame.client_message_id) && message(frame.message)
        ? [{ kind: 'message.acknowledged', clientMessageId: frame.client_message_id, message: frame.message }]
        : [];
    case 'message_nack':
      return string(frame.client_message_id)
        ? [{ kind: 'message.rejected', clientMessageId: frame.client_message_id }]
        : [];
    case 'message_edited':
      return message(frame.message) ? [{ kind: 'message.edited', message: frame.message }] : [];
    case 'message_deleted':
      return string(frame.channel_id) && string(frame.message_id) && typeof frame.deleted_at === 'number'
        ? [{ kind: 'message.deleted', channelId: frame.channel_id, messageId: frame.message_id, deletedAt: frame.deleted_at }]
        : [];
    case 'typing':
      return string(frame.channel_id) && string(frame.user_id) && string(frame.display_name)
        ? [{ kind: 'typing.changed', channelId: frame.channel_id, userId: frame.user_id, displayName: frame.display_name }]
        : [];
    case 'reaction_update':
      return string(frame.channel_id) && string(frame.message_id) && Array.isArray(frame.reactions)
        ? [{ kind: 'reaction.updated', channelId: frame.channel_id, messageId: frame.message_id, reactions: frame.reactions }]
        : [];
    case 'agent_activity':
      return [{ kind: 'agent.activity', frame }];
    default:
      return [];
  }
}
